/**
 * Porte du mot de passe d'EXPORT FIDO CXF (R8), appelee la ou l'export est scelle (`handleCxfExportSubmit`, `app.ts`).
 *
 * Le fichier CXF contient TOUS les secrets du coffre et quitte l'appareil : quiconque le recupere peut deviner son mot
 * de passe hors ligne, sans limite d'essais -- exactement le risque du mot de passe maitre (`docs/THREAT_MODEL.md` §5
 * hypothese 4). D'ou la meme regle : la jauge l'annonce a la frappe, et un mot de passe « faible » n'est accepte
 * qu'apres un consentement explicite (Bitwarden jauge de meme le mot de passe d'un export protege). Il n'est pas
 * interdit : le plancher de longueur reste celui de `encryptCxfExport`.
 *
 * Mesure = celle du mot de passe maitre (`masterPasswordStrength`) : l'e-mail du compte et les sites du produit
 * (« klavox », « santvia ») sont des mots connus -- « Klavox-export-2026 » se devine. Tout se calcule en memoire ;
 * rien n'est journalise ni transmis.
 */
import { MIN_CXF_PASSWORD_LENGTH, WeakCxfPasswordError } from './cxf.js';
import { masterPasswordStrength } from './master-password-gate.js';

/** Robustesse d'un mot de passe d'export, telle que la jauge l'affiche ; `null` sans mot de passe. */
export function exportPasswordStrength(password: string, email: string): ReturnType<typeof masterPasswordStrength> {
  return masterPasswordStrength(password, email);
}

/** Mot de passe d'export faible soumis sans le consentement explicite de l'utilisateur. */
export class UnconfirmedWeakExportPasswordError extends Error {
  readonly bits: number;

  constructor(bits: number) {
    super(
      `mot de passe d'export faible (${Math.round(bits)} bits) : le fichier contient tous vos secrets et se devine hors ligne. ` +
        'Choisissez-en un plus fort, ou cochez « Utiliser ce mot de passe faible quand meme ».',
    );
    this.name = 'UnconfirmedWeakExportPasswordError';
    this.bits = bits;
  }
}

/**
 * Porte du mot de passe d'export, avant tout dechiffrement du coffre : trop court, refuse ; faible, refuse sauf
 * consentement explicite (`acceptWeak`).
 */
export function assertExportPasswordAccepted(password: string, email: string, acceptWeak: boolean): void {
  if (password.length < MIN_CXF_PASSWORD_LENGTH) throw new WeakCxfPasswordError();
  const strength = exportPasswordStrength(password, email);
  if (strength?.label === 'faible' && !acceptWeak) throw new UnconfirmedWeakExportPasswordError(strength.bits);
}
